"use client";

import React from "react";
import SafeHtml from "@/components/ui/SafeHtml";
import Wrapper from "@/components/ui/Wrapper";
import type { FAQFields } from "@/types/acf";

function PlusMinus({ open }: { open: boolean }) {
  return (
    <span className="relative inline-flex h-4 w-4 flex-none items-center justify-center">
      <span className="absolute h-[2px] w-4 bg-current" />
      <span
        className={[
          "absolute w-[2px] h-4 bg-current transition-transform duration-200 origin-center",
          open ? "scale-y-0" : "scale-y-100",
        ].join(" ")}
      />
    </span>
  );
}

function FAQRow({
  item,
  index,
}: {
  item: { question?: string; answer?: string };
  index: number;
}) {
  const question = item?.question ?? "";
  const answer = item?.answer ?? "";
  const [open, setOpen] = React.useState(false);

  if (!question && !answer) return null;

  return (
    <button
      type="button"
      onClick={() => setOpen((v) => !v)}
      aria-expanded={open}
      aria-controls={`faq-panel-${index}`}
      className="w-full text-left bg-fuji-grey"
    >
      <div className="px-6 py-6">
        {/* lock header layout so it never shifts */}
        <div className="w-full flex items-center justify-between gap-4">
          <h3 className="text-title-4 leading-snug">{question}</h3>
          <PlusMinus open={open} />
        </div>

        {/* smoother: no mt toggling, spacing lives inside */}
        <div
          id={`faq-panel-${index}`}
          className={[
            "grid overflow-hidden",
            "transition-[grid-template-rows] duration-300 ease-in-out",
            "will-change-[grid-template-rows]",
            open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
          ].join(" ")}
        >
          <div className="min-h-0 overflow-hidden">
            <div
              className={[
                "pt-4 transform-gpu will-change-[opacity,transform]",
                "transition-[opacity,transform] duration-200 ease-in-out",
                open ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1",
              ].join(" ")}
            >
              {answer ? <SafeHtml content={answer} flow /> : null}
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}

export default function FAQ({ data }: { data: FAQFields }) {
  if (!data || data.hide) return null;

  const sectionTitle = data.title ?? "";
  const items = Array.isArray(data.items) ? data.items : [];

  if (!sectionTitle && items.length === 0) return null;

  return (
    <div id={data.id} className="w-full">
      <Wrapper>
        {sectionTitle ? (
          <h2 className="text-title-2 w-full text-center">
            <SafeHtml content={sectionTitle} />
          </h2>
        ) : null}

        <div className={sectionTitle ? "mt-4 flex flex-col gap-4" : "flex flex-col gap-4"}>
          {items.map((item, i) => (
            <FAQRow key={i} item={item ?? {}} index={i} />
          ))}
        </div>
      </Wrapper>
    </div>
  );
}
